import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';

const ManageCategoriesModal = ({
    open,
    onClose,
    onCategoriesUpdated,
    setSuccessAlertMessage,
    setSuccessAlertOpen,
    setErrorAlertMessage,
    setErrorAlertOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [addingCategory, setAddingCategory] = useState(false);
    const [deletingCategoryId, setDeletingCategoryId] = useState(null);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null); // holds category to delete

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/test-categories/with-counts`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setErrorAlertMessage('Failed to fetch categories');
            setErrorAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchCategories();
            setDeleteConfirmDialog(null);
            setNewCategoryName('');
        }
    }, [open]);

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) {
            setErrorAlertMessage('Please enter a category name');
            setErrorAlertOpen(true);
            return;
        }

        setAddingCategory(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/test-categories`,
                {
                    name: newCategoryName.trim(),
                    label: newCategoryName.trim(),
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setSuccessAlertMessage('Category added successfully!');
            setSuccessAlertOpen(true);
            setNewCategoryName('');
            fetchCategories();
            if (onCategoriesUpdated) {
                onCategoriesUpdated();
            }
        } catch (error) {
            console.error('Error adding category:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to add category');
            setErrorAlertOpen(true);
        } finally {
            setAddingCategory(false);
        }
    };

    const handleDeleteClick = (category) => {
        setDeleteConfirmDialog(category);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteConfirmDialog(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmDialog) return;

        const categoryId = deleteConfirmDialog.id;
        setDeletingCategoryId(categoryId);
        handleCloseDeleteDialog();

        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/test-categories/${categoryId}`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setSuccessAlertMessage('Category deleted successfully!');
            setSuccessAlertOpen(true);
            fetchCategories();
            if (onCategoriesUpdated) {
                onCategoriesUpdated();
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to delete category');
            setErrorAlertOpen(true);
        } finally {
            setDeletingCategoryId(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddCategory();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: colors.primary[600],
                    minHeight: '400px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h4" fontWeight="bold">
                    Manage Test Categories
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                mb: 2,
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 1,
                            }}
                        >
                            <List>
                                {categories.length === 0 ? (
                                    <ListItem>
                                        <ListItemText
                                            primary="No categories found"
                                            secondary="Add a new category below"
                                        />
                                    </ListItem>
                                ) : (
                                    categories.map((category, index) => (
                                        <React.Fragment key={category.id}>
                                            {index > 0 && <Divider />}
                                            <ListItem>
                                                <ListItemText
                                                    primary={category.name}
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {category.testsCount} test(s)
                                                        </Typography>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleDeleteClick(category)}
                                                        disabled={category.testsCount > 0 || deletingCategoryId === category.id}
                                                        title={
                                                            category.testsCount > 0
                                                                ? `Cannot delete: ${category.testsCount} test(s) using this category`
                                                                : 'Delete category'
                                                        }
                                                    >
                                                        {deletingCategoryId === category.id ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <DeleteIcon />
                                                        )}
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" mb={1}>
                            Add New Category
                        </Typography>
                        <Box display="flex" gap={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter category name"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={addingCategory}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleAddCategory}
                                disabled={addingCategory || !newCategoryName.trim()}
                                startIcon={addingCategory ? <CircularProgress size={20} /> : <AddIcon />}
                            >
                                Add
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            Note: Categories with tests cannot be deleted. Use "Bulk Delete Tests" to remove tests first.
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
            </DialogActions>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={Boolean(deleteConfirmDialog)}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: colors.primary[600],
                    },
                }}
            >
                <DialogTitle>Delete Category</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the category "{deleteConfirmDialog?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default ManageCategoriesModal;
